import { Edit } from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PopoverAG from "components/ui/popoverAG";
import useBase from "hooks/useBase";
import useFormatter from "hooks/useFormatter";
import useWindowSize from "hooks/useWindowSize";
import { setLoading } from "lib/Reducer/loadingSlice";
import moment from "moment";
import { MdDelete, MdEdit } from "react-icons/md";
import { TbListDetails } from "react-icons/tb";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { updateFollowupDate, updateLeadStatus } from "../../api/leads";
import useQueries from "hooks/useQueries";
const isGivenDateLessThanToday = (givenDate) => {
  const today = moment();
  return moment(givenDate).isBefore(today, "day");
};

const MainTableRow = ({
  row,
  columns,
  handleDelete,
  setReload,
  openNote,
  setOpenNote,
  handleTableCheckBox,
  selectedRows,
  allLeads,
  setAllLeads,
  permissions,
  ...restProps
}) => {
  const { setQueries } = useQueries();
  const { isMini } = useWindowSize();
  const { base } = useBase();
  const { leadStatuses } = base;
  const { formatDate } = useFormatter();
  const handleChangeStatus = async (id) => {
    const result = await updateLeadStatus({
      body_data: { lead_id: row?.id, lead_status_id: id },
    });
    setAllLeads(
      allLeads.map((lead) =>
        lead.id === row.id
          ? { ...row, lead_status: result.data, lead_status_id: result.data.id }
          : lead
      )
    );
  };

  // const dispatch = useDispatch();

  const handleFollowupDate = async (followup_date) => {
    // dispatch(setLoading(true));
    const body_data = {
      lead_id: row?.id,
      followup_date,
    };
    try {
      const result = await updateFollowupDate({ body_data });
      if (result.success) {
        toast.success(result.message);
        setAllLeads(
          allLeads.map((lead) =>
            lead.id === row.id ? { ...lead, ...result.data } : lead
          )
        );
      } else {
        toast.error(result.message);
      }
    } finally {
      // dispatch(setLoading(false));
    }
  };

  return (
    <TableRow
      {...restProps}
      sx={{
        "& .MuiTableCell-sizeMedium": {
          padding: "10px 0px 10px 16px",
          backgroundColor: isGivenDateLessThanToday(row.followup_date)
            ? "#fff1f1"
            : "",
        },
        "& .MuiTableCell-sizeMedium:first-of-type": {
          paddingLeft: "8px",
          paddingRight: "0px",
        },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedRows?.includes(row.id)}
          onChange={(e) => handleTableCheckBox(row.id, e.target.checked)}
          disabled={!permissions["bulk action"] || isMini}
        />
      </TableCell>
      {columns.map((column) => {
        const value = row[column.id];
        return (
          <TableCell key={column.id} align={column.align} style={column.style}>
            {column.id === "lead_status" ? (
              <TextField
                disabled={!permissions["change status"]}
                size="small"
                value={row?.lead_status?.id}
                select
                sx={{
                  width: "130px",
                  color: row?.lead_status?.color,
                  textAlign: "left",
                  "& > div:before": { content: "none" },
                  "& div": { color: row?.lead_status?.color },
                }}
                variant="standard"
                onChange={(e) => handleChangeStatus(e.target.value)}
              >
                {leadStatuses?.length > 0 &&
                  leadStatuses?.map((status) => (
                    <MenuItem value={status?.id} key={status?.id}>
                      {status?.name}
                    </MenuItem>
                  ))}
              </TextField>
            ) : column.id === "last_note" ? (
              <Box
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  "&:hover": {
                    "& svg": {
                      color: (theme) => theme.palette.primary.main,
                    },
                  },
                  "& svg": {
                    position: "absolute",
                    color:
                      openNote === row?.id
                        ? (theme) => theme.palette.primary.main
                        : "#777",
                    top: "0px",
                    right: "0px",
                    cursor: "pointer",
                    fontSize: "17px",
                  },
                }}
                onClick={() => setOpenNote(openNote === row?.id ? "" : row?.id)}
              >
                <Typography
                  sx={{
                    overflow: "hidden",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                    maxHeight: "22px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {value || "-"}
                </Typography>
                <MdEdit />
              </Box>
            ) : column.id === "followup_date" ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{formatDate(value)}</span>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Custom input"
                    value={value}
                    onChange={(newValue) => {
                      handleFollowupDate(moment(newValue).format("YYYY-MM-DD"));
                    }}
                    renderInput={({ inputRef, inputProps, InputProps }) => (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {InputProps?.endAdornment}
                        <input
                          style={{
                            width: "0px",
                            height: "0px",
                            opacity: 0,
                          }}
                          ref={inputRef}
                          {...inputProps}
                        />
                      </Box>
                    )}
                  />
                </LocalizationProvider>
              </Box>
            ) : column.id === "lead_assignees" ? (
              <PopoverAG items={value} />
            ) : column.id === "action" ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Details">
                  <IconButton onClick={() => setQueries(`id=${row?.id}&m=1`)}>
                    <TbListDetails />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    disabled={!permissions.update}
                    onClick={() => setQueries(`id=${row?.id}&m=0`)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Trash">
                  <IconButton
                    disabled={!permissions.trash}
                    onClick={() => handleDelete(row.id)}
                  >
                    <MdDelete />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              value || "-"
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default MainTableRow;
